/**
 * <s-gallery-nav> — mobile previous/next controls and slide count for the
 * product gallery scroller. Desktop hides the controls (md:hidden) and the
 * gallery renders as a grid, so this component is inert there.
 *
 * Required markup (inside the element):
 *   [data-product-gallery]          — horizontal scroll-snap container; each child is one slide.
 *   button[command="--previous"]    — commandfor points to this element's id.
 *   button[command="--next"]        — commandfor points to this element's id.
 *   [data-gallery-count-visual]     — visible "1 / N" text.
 *   [data-gallery-count-text]       — screen-reader count text.
 *   data-count-template             — translated "Image [current] of [total]" template.
 */
class SGalleryNav extends HTMLElement {
  #ac;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const gallery = this.#gallery();
    if (!gallery) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--previous') this.#go(this.#index - 1);
      if (event.command === '--next') this.#go(this.#index + 1);
    }, opts);

    gallery.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });

    window.addEventListener('resize', () => this.#sync(), opts);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #gallery() {
    return this.querySelector('[data-product-gallery]');
  }

  #slides() {
    const gallery = this.#gallery();
    return gallery ? Array.from(gallery.children) : [];
  }

  #go(target) {
    const gallery = this.#gallery();
    const slides = this.#slides();
    if (!gallery || slides.length < 2) return;

    const index = Math.max(0, Math.min(slides.length - 1, target));
    const isRtl = getComputedStyle(gallery).direction === 'rtl';
    const left = index * gallery.clientWidth * (isRtl ? -1 : 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    gallery.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#index = index;
    this.#render(slides.length);
  }

  #sync() {
    const gallery = this.#gallery();
    const slides = this.#slides();
    if (!gallery || !slides.length || !gallery.clientWidth) return;

    const index = Math.round(Math.abs(gallery.scrollLeft) / gallery.clientWidth);
    this.#index = Math.max(0, Math.min(slides.length - 1, index));
    this.#render(slides.length);
  }

  #render(total) {
    const current = this.#index + 1;
    const prev = this.querySelector('[command="--previous"]');
    const next = this.querySelector('[command="--next"]');
    prev?.setAttribute('aria-disabled', String(this.#index === 0));
    next?.setAttribute('aria-disabled', String(this.#index >= total - 1));

    const visual = this.querySelector('[data-gallery-count-visual]');
    if (visual) visual.textContent = `${current} / ${total}`;

    const text = this.querySelector('[data-gallery-count-text]');
    const template = this.dataset.countTemplate;
    if (text && template) {
      const label = template.replace('[current]', String(current)).replace('[total]', String(total));
      if (text.textContent !== label) text.textContent = label;
    }
  }
}

if (!customElements.get('s-gallery-nav')) {
  customElements.define('s-gallery-nav', SGalleryNav);
}
